import React from "react";
import { useAppSelector } from "redux/hooks";
import { css } from "../../../styled-system/css";
import { HStack, Stack } from "../../../styled-system/jsx";
import ToolTips from "utils/toolTips";
import getMetricSpec from "views/merchandise/metrics";
import type { StageAggStats } from "./types";
import { getTrendColor } from "components/custom/summaryReport/utils";
import { formatPercent } from "utils/formatNumber";

const cardStats = [
  {
    title: "# of Queries",
    valueAccessor: "count",
  },
  {
    title: "Ad Sales",
    valueAccessor: "ad_sales",
    prevValueAccessor: "ad_sales_percent",
  },
  {
    title: "Ad Spend",
    valueAccessor: "ad_spend",
    prevValueAccessor: "ad_spend_percent",
  },
  {
    title: "ACOS",
    valueAccessor: "acos",
    prevValueAccessor: "acos_delta",
  },
  {
    title: "CTR",
    valueAccessor: "ctr",
    prevValueAccessor: "click_thru_rate_delta",
  },
  {
    title: "Clicks",
    valueAccessor: "clicks",
    prevValueAccessor: "clicks_percent",
  },
  {
    title: "Impressions",
    valueAccessor: "impressions",
    prevValueAccessor: "impressions_percent",
  },
];

const Card: React.FC<CardProps> = ({ name, title, data, toolTip, cardMetrics, selected, setSelected }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  return (
    <div
      className={css({
        cursor: "pointer",
        borderRadius: "25px",
        height: "150px",
        marginEnd: "4",
        marginBottom: "3",
        padding: "4",
        backgroundColor: selected?.value === title ? "rgb(15,195,152, 0.15)" : undefined,
        boxShadow: "none",
      })}
      style={{ border: `2px solid ${selected?.value === title ? "rgb(15,195,152, 0.50)" : "rgb(238, 238, 238)"}` }}
      onClick={() => {
        setSelected(selected?.value === title ? { label: "All Queries", value: "all" } : { label: name, value: title });
      }}
    >
      <HStack>
        <Stack>
          <span className={css({ fontSize: "3xl", fontWeight: "bold" })}>
            {title.charAt(0).toUpperCase() + title.slice(1)}
            {toolTip && <ToolTips toolTip={toolTip} id={name + "tooltip"} position={"top"} />}
          </span>
        </Stack>
      </HStack>
      <HStack className={css({ display: "flex", alignItems: "center", columnGap: "5rem", height: "100%" })}>
        {cardMetrics.map((metric) => {
          const { title, valueAccessor, prevValueAccessor } = metric;

          const metricSpec = getMetricSpec({ marketPlace: marketPlace.marketPlace }, valueAccessor);
          const accessor = valueAccessor as keyof StageAggStats;
          const value = valueAccessor ? metricSpec.formatter(data[accessor]) : null;
          const prevValue = prevValueAccessor ? data[prevValueAccessor as keyof StageAggStats] : null;
          const prevValueStyle = {
            color: getTrendColor(prevValue, valueAccessor, "rgb(15, 195, 152)", "rgb(240, 60, 103)"),
            fontWeight: "bold",
          };
          return (
            <Stack key={title} className={css({ alignItems: "flex-start", fontSize: "larger", textAlign: "center" })}>
              <span style={{ whiteSpace: "nowrap" }}>{title}</span>
              <span className={css({ display: "block", fontSize: "2xl", fontWeight: "bold" })}>{value}</span>
              {prevValue !== undefined && prevValue !== null && (
                <span style={prevValueStyle}>{formatPercent(prevValue, 1, true)}</span>
              )}
            </Stack>
          );
        })}
      </HStack>
    </div>
  );
};

interface QueryStatsCardsProps {
  AggStats: Record<string, StageAggStats> | null;
  selectedStage: { label: string; value: string } | null;
  setSelectedStage: (stage: { label: string; value: string } | null) => void;
}
interface CardProps {
  name: string;
  title: string;
  data: StageAggStats;
  toolTip?: string;
  cardMetrics: Array<{
    title: string;
    valueAccessor: string;
    prevValueAccessor?: string;
  }>;
  selected: { label: string; value: string } | null;
  setSelected: (stage: { label: string; value: string } | null) => void;
}

const QueryStatsCards: React.FC<QueryStatsCardsProps> = ({ AggStats, selectedStage, setSelectedStage }) => {
  return (
    <>
      {AggStats && Object.keys(AggStats).length > 0 ?
        Object.entries(AggStats).map(([stage, stats]) => (
          <Card
            key={stage}
            cardMetrics={cardStats}
            data={stats}
            title={stage}
            name={stage}
            selected={selectedStage}
            setSelected={setSelectedStage}
          />
        ))
      : <p>No aggregate stats available</p>}
    </>
  );
};

export default QueryStatsCards;
